import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import adminService from '@/services/adminService';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ExternalLink,
  CheckCircle,
  XCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';

const DOC_STATUS = {
  pending: { label: 'Bekliyor', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  verified: { label: 'Onaylı', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  rejected: { label: 'Reddedildi', color: 'bg-red-100 text-red-800', icon: XCircle },
};

const DOC_TYPES = {
  passport: 'Pasaport',
  residence_permit: 'Oturma İzni',
  purchase_contract: 'Satış Sözleşmesi',
  tapu: 'Tapu',
  tax_certificate: 'Vergi Belgesi',
  military_permit: 'Askerlik İzni',
  power_of_attorney: 'Vekaletname',
  bank_statement: 'Banka Ekstresi',
  other: 'Diğer',
};

const PAGE_SIZE = 20;

export default function AdminDocumentsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [notes, setNotes] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    const { data, count, error } = await adminService.getDocuments({
      status: statusFilter === 'all' ? null : statusFilter,
      documentType: typeFilter === 'all' ? null : typeFilter,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    });
    if (error) {
      toast({ title: 'Hata', description: 'Belgeler yüklenemedi', variant: 'destructive' });
    } else {
      setDocuments(data || []);
      setTotalCount(count || 0);
    }
    setLoading(false);
  }, [statusFilter, typeFilter, page, toast]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleVerify = async () => {
    if (!selectedDoc || !newStatus) return;
    const { error } = await adminService.verifyDocument(selectedDoc.id, newStatus, notes || null);
    if (error) {
      toast({ title: 'Hata', description: 'Belge güncellenemedi', variant: 'destructive' });
    } else {
      toast({ title: 'Başarılı', description: 'Belge durumu güncellendi' });
      setDialogOpen(false);
      loadData();
    }
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Durum" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Durumlar</SelectItem>
                <SelectItem value="pending">Bekliyor</SelectItem>
                <SelectItem value="verified">Onaylı</SelectItem>
                <SelectItem value="rejected">Reddedildi</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Belge Türü" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Türler</SelectItem>
                {Object.entries(DOC_TYPES).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={loadData}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Belgeler ({totalCount})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : documents.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <AlertCircle className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>Belge bulunamadı</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Kullanıcı</TableHead>
                      <TableHead>Dosya</TableHead>
                      <TableHead>Tür</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead>Boyut</TableHead>
                      <TableHead>Yükleme</TableHead>
                      <TableHead className="text-right">İşlem</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {documents.map((doc) => {
                      const st = DOC_STATUS[doc.status] || DOC_STATUS.pending;
                      const StIcon = st.icon;
                      return (
                        <TableRow key={doc.id}>
                          <TableCell>
                            <button
                              onClick={() => navigate(`/admin/users/${doc.user_id}`)}
                              className="text-blue-600 hover:underline font-medium"
                            >
                              {doc.user_profile?.full_name || doc.user_id?.slice(0, 8)}
                            </button>
                          </TableCell>
                          <TableCell className="max-w-[180px] truncate">
                            <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-600 hover:underline">
                              {doc.file_name}
                              <ExternalLink className="w-3 h-3 flex-shrink-0" />
                            </a>
                          </TableCell>
                          <TableCell>{DOC_TYPES[doc.document_type] || doc.document_type}</TableCell>
                          <TableCell>
                            <Badge className={`${st.color} text-xs`}>
                              <StIcon className="w-3 h-3 mr-1" />
                              {st.label}
                            </Badge>
                          </TableCell>
                          <TableCell>{doc.file_size ? `${(doc.file_size / 1024).toFixed(0)} KB` : '-'}</TableCell>
                          <TableCell className="text-xs">{formatDate(doc.uploaded_at)}</TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" onClick={() => {
                              setSelectedDoc(doc);
                              setNewStatus(doc.status);
                              setNotes(doc.notes || '');
                              setDialogOpen(true);
                            }}>
                              Doğrula
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">{page * PAGE_SIZE + 1} - {Math.min((page + 1) * PAGE_SIZE, totalCount)} / {totalCount}</p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                    <span className="text-sm">{page + 1} / {totalPages}</span>
                    <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Belge Doğrulama</DialogTitle>
            <DialogDescription>
              {selectedDoc?.file_name} - {DOC_TYPES[selectedDoc?.document_type] || selectedDoc?.document_type}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Durum</label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Bekliyor</SelectItem>
                  <SelectItem value="verified">Onayla</SelectItem>
                  <SelectItem value="rejected">Reddet</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Notlar</label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Admin notları..." rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>İptal</Button>
            <Button onClick={handleVerify}>Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Toaster />
    </div>
  );
}
